import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import {useDispatch} from 'react-redux';
import * as reduxApi from 'src/utils/redux-api-v2';
import {Textarea} from '@spaced-out/ui-design-system/lib/components/Textarea';
import {
  Panel,
  PanelHeader,
  PanelBody,
  PanelFooter,
} from '@spaced-out/ui-design-system/lib/components/Panel';
import {
  SubTitleLarge,
  BodySmall,
  BodyMedium,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Rating} from '@spaced-out/ui-design-system/lib/components/Rating';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {
  SimpleDropdown,
  RadioGroup,
  RadioButton,
  MenuOption,
} from '@spaced-out/ui-design-system/lib/components';
import {Input} from '@spaced-out/ui-design-system/lib/components/Input';
import css from './survey.css';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {
  Toast,
  ToastTitle,
  toastApi,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import {getHmExperienceSurveys} from 'src/action-creators/crm';


type SurveyProps = {
  surveyId: string;
  isOpen: boolean;
  handleClose: () => void;
  handleRemoveSurvey?: (surveyId: string) => void;
};

const Survey = ({
  surveyId,
  isOpen,
  handleClose,
  handleRemoveSurvey,
}: SurveyProps) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const [surveyQuestions, setSurveyQuestions] = React.useState([]);
  const [feedbackData, setFeedbackData] = React.useState<any>(null);
  const [answers, setAnswers] = React.useState([]);
  const [rating, setRating] = React.useState<any>();
  const [loading, setLoading] = React.useState(true);
  const dispatch = useDispatch();

  React.useEffect(() => {
    const fetchSurveyQuestions = async () => {
      const response = await dispatch(
        reduxApi.get(`api/hm-survey-configuration`, {}, {apiPath: '/trmapi/'}),
      );
      // @ts-expect-error - TS2345 - Argument of type 'ThunkAction<any>' is not assignable to parameter of type 'SetStateAction<never[]>'.
      setSurveyQuestions(response);
      setLoading(false);
    };
    fetchSurveyQuestions();
    const fetchFeedbackData = async () => {
      const response = await dispatch(
        reduxApi.get(`api/hm-feedback/${surveyId}`, {}, {apiPath: '/trmapi/'}),
      );
      setFeedbackData(response);
      setLoading(false);
    };
    fetchFeedbackData();
  }, []);

  const handleAnswerChange = (
    questionId: any,
    value: MenuOption | number | string,
  ) => {
    // @ts-expect-error - TS2345 - Argument of type '(prevAnswers: never[]) => { questionId: any; answer: any; }[]' is not assignable to parameter of type 'SetStateAction<never[]>'.
    setAnswers((prevAnswers) => {
      const existingAnswerIndex = prevAnswers.findIndex(
        // @ts-expect-error - TS2339 - Property 'questionId' does not exist on type 'never'.
        (answer) => answer.questionId === questionId,
      );

      if (existingAnswerIndex >= 0) {
        // Update existing answer
        const updatedAnswers = [...prevAnswers];
        // @ts-expect-error - TS2322 - Type '{ questionId: any; answer: any; }' is not assignable to type 'never'.
        updatedAnswers[existingAnswerIndex] = {
          questionId,
          answer: value.toString(),
        };
        return updatedAnswers;
      } else {
        // Add new answer
        return [
          ...prevAnswers,
          {
            questionId,
            answer: value.toString(),
          },
        ];
      }
    });
  };

  const handleSubmitFeedback = async () => {
    setLoading(true);

    try {
      await dispatch(
        reduxApi.post(
          `api/hm-feedback/${surveyId}`,
          {answers},
          {},
          {apiPath: '/trmapi/'},
        ),
      );
      handleRemoveSurvey?.(surveyId);
      handleClose();
    } catch (error: any) {
      console.error('Error submitting feedback:', error);
    }

    setLoading(false);
    const successToast = (
      <Toast initialFocus={0} semantic={'success'}>
        <ToastTitle>
          {t(
            'Feedback_submitted_successfully',
            'Feedback submitted successfully',
          )}
        </ToastTitle>
      </Toast>
    );
    toastApi.show(successToast);
  };

  const hasInvalidAnswers = () => {
    return (
      surveyQuestions
        // @ts-expect-error - TS2339 - Property 'showStatus' does not exist on type 'never'.
        .filter((question) => question.showStatus === 'MANDATORY')
        .some(
          (question) =>
            !answers.some(
              (answer) =>
                // @ts-expect-error - TS2339 - Property 'questionId' does not exist on type 'never'. | TS2339 - Property 'id' does not exist on type 'never'.
                answer.questionId === question.id &&
                // @ts-expect-error - TS2339 - Property 'answer' does not exist on type 'never'.
                answer.answer &&
                // @ts-expect-error - TS2339 - Property 'answer' does not exist on type 'never'.
                answer.answer.trim() !== '',
            ),
        )
    );
  };

  return (
    <Panel
      anchor="right"
      initialFocus={1}
      onClose={handleClose}
      size="large"
      tapOutsideToClose
      isOpen={isOpen}
      hideBackdrop={false}
    >
      <PanelHeader onCloseButtonClick={handleClose} size="medium">
        <SubTitleLarge>
          {t(
            'Submit_Hiring_Manager_Survey_Feedback',
            'Submit Hiring Manager Survey Feedback',
          )}
        </SubTitleLarge>
      </PanelHeader>
      {loading ? (
        <CircularLoader className={css.loader} size="large" />
      ) : (
        <PanelBody className={css.questionContainer}>
          <BodyMedium>
            <b>
              {t(
                'Tell_us_about_your_experience_with_our_hiring_process',
                'Tell us about your experience with our hiring process',
              )}
            </b>
          </BodyMedium>
          {surveyQuestions
            .filter(
              // @ts-expect-error - TS2339 - Property 'default' does not exist on type 'never'. | TS2339 - Property 'showStatus' does not exist on type 'never'.
              (question) => !question.default && question.showStatus !== 'OFF',
            )
            .map((question, index) => (
              // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'never'.
              <div key={question.id} className={css.column}>
                <BodySmall color="secondary">
                  {/* @ts-expect-error - TS2339 - Property 'name' does not exist on type 'never'. */}
                  {`${t('Q', 'Q')}${index + 1}. ${question.name}`}
                  {/* @ts-expect-error - TS2339 - Property 'showStatus' does not exist on type 'never'. */}
                  {question.showStatus === 'MANDATORY' && ' *'}
                </BodySmall>
                {/* @ts-expect-error - TS2339 - Property 'description' does not exist on type 'never'. */}
                {question.description && (
                  <BodySmall color="secondary">
                    {/* @ts-expect-error - TS2339 - Property 'description' does not exist on type 'never'. */}
                    {question.description}
                  </BodySmall>
                )}
                {/* @ts-expect-error - TS2339 - Property 'fieldType' does not exist on type 'never'. */}
                {question.fieldType === 'RATING' && (
                  <div className={css.column}>
                    <Rating
                      hideLabel
                      onChange={(value) => {
                        // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'never'.
                        handleAnswerChange(question.id, value);
                        setRating(value);
                      }}
                      rating={rating}
                    />
                  </div>
                )}

                {/* @ts-expect-error - TS2339 - Property 'fieldType' does not exist on type 'never'. */}
                {question.fieldType === 'TEXT' && (
                  <Input
                    // label={question.name}
                    placeholder=""
                    size="medium"
                    onChange={(e) =>
                      // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'never'.
                      handleAnswerChange(question.id, e.target.value)
                    }
                    value={
                      feedbackData?.answers?.find(
                        // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'never'.
                        (a: any) => a.questionId === question.id,
                      )?.answer || ''
                    }
                  />
                )}

                {/* @ts-expect-error - TS2339 - Property 'fieldType' does not exist on type 'never'. */}
                {question.fieldType === 'TEXT_AREA' && (
                  <Textarea
                    // label={question.name}
                    placeholder={t(
                      'Enter_your_response',
                      'Enter your response',
                    )}
                    onChange={(e) =>
                      // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'never'.
                      handleAnswerChange(question.id, e.target.value)
                    }
                    value={
                      //@ts-expect-error
                      //prettier-ignore
                      answers.find((a) => a.questionId === question.id)
                      // @ts-expect-error - TS2339 - Property 'questionId' does not exist on type 'never'.
                        ?.answer || ''
                    }
                    // @ts-expect-error - TS2769 - No overload matches this call.
                    rows={4}
                  />
                )}

                {/* @ts-expect-error - TS2339 - Property 'fieldType' does not exist on type 'never'. */}
                {(question.fieldType === 'SINGLE_SELECT' ||
                  // @ts-expect-error - TS2339 - Property 'fieldType' does not exist on type 'never'.
                  question.fieldType === 'MULTI_SELECT') && (
                  <SimpleDropdown
                    // label={question.name}
                    options={
                      // @ts-expect-error - TS2339 - Property 'options' does not exist on type 'never'.
                      question.options?.map((option: any, i: any) => ({
                        key: i.toString(),
                        label: option,
                        value: option,
                      })) || []
                    }
                    placeholder={`${t('Select_option', 'Select option')}`}
                    // @ts-expect-error - TS2769 - No overload matches this call. | TS2339 - Property 'fieldType' does not exist on type 'never'.
                    isMulti={question.fieldType === 'MULTI_SELECT'}
                    // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'never'.
                    onChange={(value) => handleAnswerChange(question.id, value)}
                    value={
                      feedbackData?.answers?.find(
                        // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'never'.
                        (a: any) => a.questionId === question.id,
                      )?.answer || ''
                    }
                  />
                )}

                {/* @ts-expect-error - TS2339 - Property 'fieldType' does not exist on type 'never'. */}
                {question.fieldType === 'BOOLEAN' && (
                  <div className={css.column}>
                    <RadioGroup
                      align="horizontal-fluid"
                      // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'never'.
                      name={`question_${question.id}`}
                      value={
                        // @ts-expect-error - TS2339 - Property 'questionId' does not exist on type 'never'. | TS2339 - Property 'id' does not exist on type 'never'.
                        //prettier-ignore
                        answers.find((a) => a.questionId === question.id)
                        // @ts-expect-error - TS2339 - Property 'answer' does not exist on type 'never'.
                          ?.answer || ''
                      }
                      onChange={(value) =>
                        // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'never'.
                        handleAnswerChange(question.id, value)
                      }
                    >
                      <RadioButton value="true">{t('Yes', 'Yes')}</RadioButton>
                      <RadioButton value="false">{t('No', 'No')}</RadioButton>
                    </RadioGroup>
                  </div>
                )}

                {/* @ts-expect-error - TS2339 - Property 'fieldType' does not exist on type 'never'. */}
                {question.fieldType === 'DATE' && (
                  <Input
                    type="date"
                    size="medium"
                    onChange={(e) =>
                      // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'never'.
                      handleAnswerChange(question.id, e.target.value)
                    }
                    value={
                      // @ts-expect-error - TS2339 - Property 'questionId' does not exist on type 'never'. | TS2339 - Property 'id' does not exist on type 'never'.
                      //prettier-ignore
                      answers.find((a) => a.questionId === question.id)
                      // @ts-expect-error - TS2339 - Property 'answer' does not exist on type 'never'.
                        ?.answer || ''
                    }
                  />
                )}

                {/* @ts-expect-error - TS2339 - Property 'fieldType' does not exist on type 'never'. */}
                {question.fieldType === 'NUMBER' && (
                  <Input
                    // label={question.name}
                    type="number"
                    placeholder={t('Enter_a_number', 'Enter a number')}
                    size="medium"
                    onChange={(e) => {
                      const value = e.target.value;
                      // Only allow valid numbers
                      // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'number'.
                      if (value === '' || !isNaN(value)) {
                        // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'never'.
                        handleAnswerChange(question.id, value);
                      }
                    }}
                    value={
                      // @ts-expect-error - TS2339 - Property 'questionId' does not exist on type 'never'. | TS2339 - Property 'id' does not exist on type 'never'.
                      //prettier-ignore
                      answers.find((a) => a.questionId === question.id)
                      // @ts-expect-error - TS2339 - Property¯ 'answer' does not exist on type 'never'.
                        ?.answer || ''
                    }
                  />
                )}
              </div>
            ))}
        </PanelBody>
      )}
      <PanelFooter>
        <div className={css.footerButtons}>
          <Button type="ghost" onClick={handleClose}>
            {t('Discard', 'Discard')}
          </Button>
          <Button
            type="primary"
            disabled={loading || hasInvalidAnswers()}
            onClick={handleSubmitFeedback}
          >
            {t('Submit', 'Submit')}
          </Button>
        </div>
      </PanelFooter>
    </Panel>
  );
};

export default Survey;
